import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

const EMAIL = 'gws@genwsociety.com';
const LAST_UPDATED = 'November 2026';
const APP_STORE_URL = 'https://apps.apple.com/app/generational-wealth-society/id6753958038';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.karmanbeyond.gws&hl=en';

const Mail = () => (
  <a href={`mailto:${EMAIL}`} className="text-[#1a441f] font-medium underline underline-offset-4 decoration-[#e4a483] hover:text-[#e4a483] transition-colors">
    {EMAIL}
  </a>
);

const PRIVACY = {
  title: 'Privacy Policy',
  metaTitle: 'Privacy Policy · GWS App',
  intro: (
    <>
      This Privacy Policy explains how the Generational Wealth Society ("GWS", "we", "us") collects, uses and protects
      personal information when you use the GWS mobile application on iOS and Android (the "App") and related services.
      By using the App, you agree to the practices described here.
    </>
  ),
  sections: [
    {
      title: 'Information We Collect',
      body: (
        <>
          <p>Information you provide directly to us, including:</p>
          <ul>
            <li>Account details such as your name, email address and phone number.</li>
            <li>Profile information such as your photo, company, role, biography, interests and social links.</li>
            <li>Event registrations, guest details, RSVPs and preferences you submit through the App.</li>
            <li>Content you share with other members, and messages you send to us.</li>
          </ul>
          <p>Information collected automatically or with your permission:</p>
          <ul>
            <li>Usage information, such as the screens you visit and the features you use, and basic device information (device model, operating system, app version).</li>
            <li>A push notification token, if you allow notifications.</li>
            <li>Your approximate or precise location while the App is in use, only if you grant permission, to show nearby events and members on the map.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Device Permissions',
      body: (
        <>
          <p>The App asks for permission before accessing the following features. You can change these at any time in your device settings.</p>
          <ul>
            <li><strong>Location</strong> — to show nearby events and members on the map.</li>
            <li><strong>Calendar</strong> — to add events you register for to your calendar.</li>
            <li><strong>Camera and photos</strong> — to scan event QR codes and to choose or upload a profile photo.</li>
            <li><strong>Notifications</strong> — to send you updates about events, connections and the community.</li>
            <li><strong>Face ID / biometrics</strong> — to unlock the App, if you enable it. Biometric data is handled entirely by your device's operating system and is never sent to us.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      body: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Create and manage your membership and account.</li>
            <li>Provide, maintain and improve the App and our events.</li>
            <li>Facilitate connections between members of the community.</li>
            <li>Process event registrations and communicate with you about them.</li>
            <li>Send you service messages, notifications and, where permitted, updates about the Society.</li>
            <li>Keep the community safe, prevent misuse and comply with legal obligations.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Information Sharing',
      body: (
        <>
          <p>
            Your profile information is visible to other approved members of the GWS community. You can control parts of
            your visibility through the App's Privacy &amp; Security settings.
          </p>
          <p>
            We do not sell your personal information, and we do not share it with third parties for their own marketing
            purposes. We share information only with:
          </p>
          <ul>
            <li>Service providers that host and operate the App on our behalf (for example database, authentication and file storage, maps, push notification and email delivery providers), under appropriate confidentiality and data protection terms.</li>
            <li>Event partners and venues, where necessary to organise an event you have registered for.</li>
            <li>Authorities or other parties where required by law, or to protect the rights, safety and property of GWS, our members or others.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Data Retention',
      body: (
        <p>
          We keep your information for as long as your membership is active and as needed to provide the App. When your
          account is deactivated, we delete or anonymise your personal information within a reasonable period, except
          where we need to keep certain records to meet legal, accounting or security obligations.
        </p>
      ),
    },
    {
      title: 'Data Security',
      body: (
        <p>
          We implement appropriate technical and organisational measures to protect your personal information against
          unauthorised access, alteration, disclosure or destruction, including encrypted connections and access
          controls. No method of transmission or storage is completely secure, but we work to protect your information.
        </p>
      ),
    },
    {
      title: 'Your Rights & Account Deletion',
      body: (
        <>
          <p>
            You have the right to access, correct or delete your personal information, and to object to or restrict
            certain processing. Depending on where you live (for example the UK, EU or Türkiye), you may have additional
            rights under applicable data protection laws, including the right to lodge a complaint with your local
            supervisory authority.
          </p>
          <p>
            You can deactivate your account at any time from your profile settings in the App (<strong>Deactivate account</strong>),
            or by emailing <Mail />. You can also ask us to delete your data by contacting us at the
            same address.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      body: (
        <p>
          The App is intended for adults and is not directed to children under 18. We do not knowingly collect personal
          information from children. If you believe a child has provided us with information, please contact us and we
          will delete it.
        </p>
      ),
    },
    {
      title: 'Changes to This Policy',
      body: (
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date above
          and, where the changes are significant, notify you through the App or by email.
        </p>
      ),
    },
  ],
};

const TERMS = {
  title: 'Terms of Service',
  metaTitle: 'Terms of Service · GWS App',
  intro: (
    <>
      These Terms of Service ("Terms") govern your access to and use of the Generational Wealth Society ("GWS", "we",
      "us") mobile application on iOS and Android (the "App") and related services. Please read them carefully.
    </>
  ),
  sections: [
    {
      title: 'Acceptance of Terms',
      body: (
        <p>
          By accessing and using the App, you accept and agree to be bound by these Terms and by our{' '}
          <Link to="/app-privacy-policy" className="text-[#1a441f] font-medium underline underline-offset-4 decoration-[#e4a483] hover:text-[#e4a483] transition-colors">
            Privacy Policy
          </Link>
          . If you do not agree, please do not use the App.
        </p>
      ),
    },
    {
      title: 'Membership',
      body: (
        <>
          <p>
            Membership to GWS is by invitation or application only, and is subject to approval. You must be at least 18
            years old to use the App.
          </p>
          <p>
            All members must maintain professional conduct and respect the privacy and confidentiality of other members.
            Information shared within the community, including member profiles and conversations, must not be disclosed
            outside the community without consent.
          </p>
        </>
      ),
    },
    {
      title: 'Your Account',
      body: (
        <p>
          You are responsible for keeping your login details secure and for all activity under your account. Please
          provide accurate information and keep it up to date. Notify us promptly at <Mail /> if you believe your account
          has been accessed without authorisation.
        </p>
      ),
    },
    {
      title: 'User Content',
      body: (
        <>
          <p>
            Members are responsible for the content they share on the App. Content must be appropriate, lawful and
            respectful of others. You may not post content that is harassing, defamatory, discriminatory, misleading,
            infringing or otherwise objectionable, and you may not use the App to solicit or advertise without our
            consent.
          </p>
          <p>
            By sharing content in the App, you grant GWS a non-exclusive, royalty-free licence to host, display and
            distribute that content within the App for the purpose of operating the service. We may remove content that
            violates these Terms.
          </p>
        </>
      ),
    },
    {
      title: 'Events',
      body: (
        <p>
          Events listed in the App may be organised by GWS or with partners. Registration, availability, pricing and
          cancellation terms for each event are shown at the time of registration. Attendance is at your own discretion,
          and you agree to follow venue rules and the reasonable instructions of the organisers.
        </p>
      ),
    },
    {
      title: 'Intellectual Property',
      body: (
        <p>
          All content, features and functionality of the App, excluding content provided by members, are owned by the
          Generational Wealth Society or its licensors and are protected by international copyright, trademark and other
          intellectual property laws.
        </p>
      ),
    },
    {
      title: 'Suspension & Termination',
      body: (
        <p>
          You may stop using the App and deactivate your account at any time from within the App. We may suspend or
          end your access if you breach these Terms, if your membership is not renewed or approved, or where necessary to
          protect the community.
        </p>
      ),
    },
    {
      title: 'Disclaimers & Limitation of Liability',
      body: (
        <>
          <p>
            The App is provided "as is" and "as available". Content shared in the App, including by other members, is for
            general information only and does not constitute financial, investment, legal or tax advice.
          </p>
          <p>
            To the fullest extent permitted by law, GWS is not liable for any indirect, incidental or consequential loss
            arising from your use of the App or from interactions with other members. Nothing in these Terms limits
            liability that cannot be limited under applicable law.
          </p>
        </>
      ),
    },
    {
      title: 'App Stores',
      body: (
        <p>
          If you downloaded the App from the Apple App Store or Google Play, your use is also subject to their applicable
          terms. Apple and Google are not parties to these Terms and are not responsible for the App or its content.
        </p>
      ),
    },
    {
      title: 'Changes to These Terms',
      body: (
        <p>
          We may update these Terms from time to time. When we do, we will revise the "Last updated" date above.
          Continued use of the App after changes take effect means you accept the updated Terms.
        </p>
      ),
    },
  ],
};

const LegalPage = ({ type }) => {
  const doc = type === 'terms' ? TERMS : PRIVACY;
  const other = type === 'terms'
    ? { to: '/app-privacy-policy', label: 'Privacy Policy' }
    : { to: '/app-terms', label: 'Terms of Service' };

  useEffect(() => {
    const prev = document.title;
    document.title = doc.metaTitle;
    window.scrollTo(0, 0);
    return () => { document.title = prev; };
  }, [doc.metaTitle]);

  return (
    <div className="relative min-h-screen bg-[#f5f3f0]">
      <Navbar textColor="green" logoVariant="green" hoverColor="copper" />

      <main className="relative z-10 px-6 pt-32 pb-24 md:pt-40 lg:px-16">
        <article className="max-w-3xl mx-auto" style={{ fontFamily: "'Figtree', sans-serif" }}>
          <p className="text-[#e4a483] text-xs md:text-sm tracking-[0.4em] font-medium uppercase mb-4">
            GWS App · Last updated {LAST_UPDATED}
          </p>
          <h1
            className="text-[#1a441f] text-4xl md:text-6xl leading-tight font-light mb-8"
            style={{ fontFamily: "'Spirits Soft', serif" }}
          >
            {doc.title}
          </h1>
          <p className="text-[#2c2c2c] text-base md:text-lg leading-relaxed font-light border-l-4 border-[#e4a483] pl-6 mb-14">
            {doc.intro}
          </p>

          <div className="space-y-12">
            {doc.sections.map((s, i) => (
              <section key={s.title}>
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-[#e4a483] text-xs tracking-[0.15em] font-mono">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-[#1a441f] text-xl md:text-2xl font-semibold">{s.title}</h2>
                </div>
                <div className="legal-body text-[#2c2c2c] text-[1rem] leading-relaxed font-light space-y-4">
                  {s.body}
                </div>
              </section>
            ))}

            <section>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="text-[#e4a483] text-xs tracking-[0.15em] font-mono">
                  {String(doc.sections.length + 1).padStart(2, '0')}
                </span>
                <h2 className="text-[#1a441f] text-xl md:text-2xl font-semibold">Contact Us</h2>
              </div>
              <p className="text-[#2c2c2c] text-[1rem] leading-relaxed font-light">
                Questions about our Terms or Privacy Policy? Write to <Mail />.
              </p>
            </section>
          </div>

          <div className="mt-16 pt-8 border-t border-[#1a441f]/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm">
            <Link to={other.to} className="text-[#1a441f] font-medium hover:text-[#e4a483] transition-colors">
              Read our {other.label} →
            </Link>
            <div className="flex gap-6 text-[#1a441f]/70">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#e4a483] transition-colors">App Store</a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#e4a483] transition-colors">Google Play</a>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
};

export default LegalPage;
